/**
 * Utility functions for handling HATEOAS links and responses
 */

/**
 * Get a specific link from a HATEOAS _links object
 * @param {Object} links - HATEOAS _links object 
 * @param {string} rel - Relation name to find
 * @returns {Object|null} Link object or null if not found
 */
export const getLink = (links, rel) => {
  if (!links || !links[rel]) {
    return null;
  }
  return links[rel];
};

/**
 * Get href from a HATEOAS link by relation
 * @param {Object} links - HATEOAS _links object
 * @param {string} rel - Relation name to find
 * @returns {string|null} The href URL or null if not found
 */
export const getLinkHref = (links, rel) => {
  const link = getLink(links, rel);
  return link ? link.href : null;
};

/**
 * Check if a question has been answered
 * @param {Object} response - Question response object
 * @returns {boolean} True if answered, false otherwise
 */
export const isQuestionAnswered = (response) => {
  return response?.answerIds && response.answerIds.length > 0;
};

/**
 * Check if a question has been skipped
 * @param {Object} response - Question response object
 * @returns {boolean} True if skipped, false otherwise
 */
export const isQuestionSkipped = (response) => {
  return response?.timeSpent > 0 && (!response.answerIds || response.answerIds.length === 0);
};

/**
 * Count completed questions (answered or skipped)
 * @param {Array} responses - Array of question responses
 * @returns {number} Number of completed questions
 */
export const countCompletedQuestions = (responses) => {
  if (!responses || !Array.isArray(responses)) {
    return 0;
  }
  return responses.reduce((count, response) => {
    return count + (response.timeSpent > 0 ? 1 : 0);
  }, 0);
};

/**
 * Format elapsed time in seconds to minutes:seconds format
 * @param {number} seconds - Time in seconds
 * @returns {string} Formatted time string (MM:SS)
 */
export const formatElapsedTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
};

/**
 * Calculate time spent on a question
 * @param {number} startTime - Start time in milliseconds
 * @returns {number} Time spent in seconds
 */
export const calculateTimeSpent = (startTime) => {
  return Math.floor((Date.now() - startTime) / 1000);
};

/**
 * Find a question by ID in an array of questions
 * @param {Array} questions - Array of questions
 * @param {number|string} questionId - ID of the question to find
 * @returns {Object|undefined} Question object or undefined if not found
 */
export const findQuestionById = (questions, questionId) => {
  return questions.find(q => q.id === questionId);
};

/**
 * Find a response for a question
 * @param {Array} responses - Array of responses
 * @param {number|string} questionId - ID of the question
 * @returns {Object|undefined} Response object or undefined if not found
 */
export const findResponseByQuestionId = (responses, questionId) => {
  return responses.find(r => r.questionId === questionId);
};
